import { router } from "@inertiajs/react";
import React, { useMemo, useState } from "react";
import { AiOutlineThunderbolt } from "react-icons/ai";
import { TbWallet } from "react-icons/tb";

import FaqCardComponent from "@/components/Cards/FaqComponent";
import Dropdown from "@/components/Dropdown";
import Layout from "@/components/Layout";
import Typography from "@/components/Typography";
import DebtsSection from "@/pages/manager/home/sections/DebtsSection";
import ExpensesGraph from "@/pages/manager/home/sections/ExpensesGraph";
import { bigNumberFormatter } from "@/utils/bigNumberFormatter";
import { moneyFormatterWithoutCurrency } from "@/utils/moneyFormat";

import {
  BarGraph,
  BreakDiv,
  DatesSecondDiv,
  DebtsDiv,
  FirstDiv,
  FourthDiv,
  Graph,
  GraphInfo,
  GraphPercentage,
  IconDiv,
  InformationBox,
  Line,
  Page,
  SecondDiv,
  StartDiv,
  ThirdDiv,
  Title,
  ValuesDiv,
} from "./styles";

interface HomeInfoProps {
  power_plants_count: number;
  consumers_count: number;
  energy_expenses_info: {
    total: number;
    paid: number;
    paid_value: number;
    to_pay: number;
    to_pay_value: number;
    overdue: number;
    overdue_value: number;
  };
  expenses_info: {
    this_month: number;
    this_year: number;
  };
  consumption_info: {
    consumed: number;
    injected: number;
  };
  debts_info: {
    total: number;
    count: number;
    consumers_count: number;
  };
  conglomerates: {
    name: string;
  }[];
}

import { getYearOptions } from "@/utils/getYearOptions";
const yearsOptions: string[] = getYearOptions();
const monthOptions: string[] = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const Home = ({ info }: { info: HomeInfoProps }) => {
  const today = new Date();
  const urlParams = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    year: urlParams.get("year") ? String(urlParams.get("year")) : String(today.getFullYear()),
    month: urlParams.get("month")
      ? monthOptions[Number(urlParams.get("month")) - 1]
      : monthOptions[today.getMonth() - 1],
    conglomerate: urlParams.get("conglomerate") ? String(urlParams.get("conglomerate")) : "Todos",
  });

  const changeFilters = ({
    year = filters.year,
    month = filters.month,
    conglomerate = filters.conglomerate,
  }) => {
    setFilters({ year, month, conglomerate });
    router.reload({
      data: {
        year: year,
        month: monthOptions.indexOf(month) + 1,
        conglomerate: conglomerate === "Todos" ? undefined : conglomerate,
      },
    });
  };

  const conglomeratesOptions = useMemo(
    () => ["Todos", ...info.conglomerates.map((conglomerate) => conglomerate.name)],
    [info.conglomerates],
  );

  return (
    <Layout>
      <FirstDiv>
        <StartDiv>
          <div className="flex w-full justify-end gap-4">
            <Dropdown
              options={conglomeratesOptions}
              onSelect={(value) => {
                changeFilters({ conglomerate: value });
              }}
              defaultValue={
                urlParams.get("conglomerate") ? String(urlParams.get("conglomerate")) : "Todos"
              }
            />
            <Dropdown
              options={yearsOptions}
              onSelect={(value) => {
                changeFilters({ year: value });
              }}
              defaultValue={
                urlParams.get("year") ? String(urlParams.get("year")) : String(today.getFullYear())
              }
            />
            <Dropdown
              options={monthOptions}
              onSelect={(value) => {
                changeFilters({ month: value });
              }}
              defaultValue={
                urlParams.get("month")
                  ? monthOptions[Number(urlParams.get("month")) - 1]
                  : monthOptions[today.getMonth() - 1]
              }
            />
          </div>
          <div className="flex w-full justify-between">
            <Typography variant="h4" color="white">
              Dados gerais
            </Typography>
            <div className="col">
              <DatesSecondDiv>
                <BreakDiv>
                  <Typography variant="overline" color="primary200">
                    USINAS
                  </Typography>
                  <Typography variant="h3" color="white">
                    {info.power_plants_count}
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="primary200">
                    CLIENTES
                  </Typography>
                  <Typography variant="h3" color="white">
                    {info.consumers_count}
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="primary200">
                    REGIÃO ATENDIDA
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    sem dados
                  </Typography>
                </BreakDiv>
              </DatesSecondDiv>
            </div>
          </div>
        </StartDiv>
      </FirstDiv>
      <SecondDiv>
        <Typography variant="h4" color="white">
          Financeiro
        </Typography>
        <InformationBox>
          <BreakDiv>
            <Typography variant="h6" color="grey500">
              Faturamento em {filters.month}
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <TbWallet size={20} color="black" />
              </IconDiv>
              <Typography variant="h3" color="grey500">
                R$
                <Typography variant="h3" color="white">
                  {" "}
                  {moneyFormatterWithoutCurrency(info.expenses_info.this_month)}
                </Typography>
              </Typography>
            </ValuesDiv>
          </BreakDiv>
          <BreakDiv>
            <Typography variant="h6" color="grey500">
              Faturamento em {filters.year}
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <TbWallet size={20} color="black" />
              </IconDiv>
              <Typography variant="h3" color="grey500">
                R$
                <Typography variant="h3" color="white">
                  {" "}
                  {moneyFormatterWithoutCurrency(info.expenses_info.this_year)}
                </Typography>
              </Typography>
            </ValuesDiv>
          </BreakDiv>
        </InformationBox>
      </SecondDiv>
      <ThirdDiv>
        <Typography variant="h4" color="white">
          Energia
        </Typography>
        <InformationBox>
          <BreakDiv>
            <Typography variant="h6" color="grey500">
              Energia consumida em {filters.month}
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <AiOutlineThunderbolt size={22} color="black" />
              </IconDiv>
              <Typography variant="h3" color="white">
                {bigNumberFormatter(info.consumption_info.consumed)}{" "}
                <Typography variant="h3" color="grey500">
                  kWh
                </Typography>
              </Typography>
            </ValuesDiv>
          </BreakDiv>
          <BreakDiv>
            <Typography variant="h6" color="grey500">
              Energia injetada em {filters.month}
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <AiOutlineThunderbolt size={22} color="black" />
              </IconDiv>
              <Typography variant="h3" color="white">
                {bigNumberFormatter(info.consumption_info.injected)}{" "}
                <Typography variant="h3" color="grey500">
                  kWh
                </Typography>
              </Typography>
            </ValuesDiv>
          </BreakDiv>
        </InformationBox>
        <BarGraph>
          <Typography variant="h6" color="grey500">
            Energia consumida X Energia Injetada
          </Typography>
        </BarGraph>
        <div>
          <Graph>
            <GraphPercentage
              percentage={
                info.consumption_info.injected
                  ? Math.min(
                      100,
                      (info.consumption_info.consumed / info.consumption_info.injected) * 100,
                    )
                  : 0
              }
            />
          </Graph>
          <GraphInfo>
            <BreakDiv>
              <Typography variant="overline" color="grey500">
                CONSUMIDA
              </Typography>
              <Typography variant="h5" color="white">
                {bigNumberFormatter(info.consumption_info.consumed)}{" "}
                <Typography variant="h5" color="grey500">
                  kWh
                </Typography>
              </Typography>
            </BreakDiv>
            <BreakDiv>
              <Typography variant="overline" color="grey500">
                INJETADA
              </Typography>
              <Typography variant="h5" color="white">
                {bigNumberFormatter(info.consumption_info.injected)}{" "}
                <Typography variant="h5" color="grey500">
                  kWh
                </Typography>
              </Typography>
            </BreakDiv>
          </GraphInfo>
        </div>
      </ThirdDiv>
      <FourthDiv>
        <Title>
          <Typography variant="h4" color="white">
            Contas e valores
          </Typography>
        </Title>

        <ExpensesGraph
          period={`${filters.month} de ${filters.year}`}
          paidExpenses={info.energy_expenses_info.paid}
          toPayExpenses={info.energy_expenses_info.to_pay}
          overdueExpenses={info.energy_expenses_info.overdue}
          paidValue={info.energy_expenses_info.paid_value}
          toPayValue={info.energy_expenses_info.to_pay_value}
          overdueValue={info.energy_expenses_info.overdue_value}
        />
      </FourthDiv>
      <DebtsDiv>
        <DebtsSection
          total={info.debts_info.total}
          count={info.debts_info.count}
          consumersCount={info.debts_info.consumers_count}
          showDetailsLink
        />
      </DebtsDiv>
      <Page>
        <FaqCardComponent variant="questionSection" />
      </Page>
    </Layout>
  );
};

export default Home;

// export const getServerSideProps: GetServerSideProps = async ctx => {
//   const { currentUser } = parseCookies(ctx);
//
//   const { id } = await JSON.parse(currentUser);
//
//   const res = await Managers.homeInfo(id, ctx);
//   const information = res.data;
//
//   return {
//     props: {
//       info: information,
//     },
//   };
// };
